/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from 'react';

import AiManagementProposalDeclarationBody, {
  DeclarationSavePayload,
} from 'components/molecules/ai_management_proposal_declaration_body';
import AiManagementProposalGuaranteeBody from 'components/molecules/ai_management_proposal_guarantee_body';
import { useProposalShell } from 'components/organisms/ai_management_bidding_proposal/context';
import type { RequirementBusy } from 'hooks/useProposalDocuments';
import { proposalRequirementTarget } from 'services/ai_management_proposal';
import type { ProposalGuaranteeModalidade } from 'services/actions/ai_management_proposal_documents';
import { formatDate } from 'services/ai_management_dates';
import { ProposalRequirement, REQUIREMENT_STATUS } from 'services/ai_management_proposal_documents';
import { Container } from './styles';

// O back aceita só PDF e DOCX nos arquivos da proposta (ProposalDocumentStorage).
export const PROPOSAL_FILE_ACCEPT = '.pdf,.docx';

interface AiManagementProposalRequirementProps {
  requirement: ProposalRequirement;
  busy?: RequirementBusy;
  error?: string;
  onOpenEdital?: () => void;
  onUpload: (file: File) => void;
  onUploadDeclarationFile?: (file: File) => Promise<unknown>;
  onRemoveFile: (fileKey: string) => void;
  onConfirm: () => void;
  onIgnore: () => void;
  onReopen: () => void;
  onSaveDeclaration?: (payload: DeclarationSavePayload) => Promise<unknown>;
  biddingId?: number;
  editalNumber?: string | null;
  onUploadGuarantee?: (file: File, modalidade: ProposalGuaranteeModalidade) => void;
  canQuoteGuarantee?: boolean;
  onOpenQuote?: () => void;
  sendGuaranteeDocumentRequest?: number;
}

const subtitleOf = (requirement: ProposalRequirement) => {
  if (requirement.status !== 'ignorada') return null;
  if (requirement.ignoredBy && requirement.ignoredAt) {
    return `Ignorada por ${requirement.ignoredBy} em ${formatDate(requirement.ignoredAt)}`;
  }
  if (requirement.ignoredBy) return `Ignorada por ${requirement.ignoredBy}`;
  return null;
};

const AiManagementProposalRequirement: React.FC<AiManagementProposalRequirementProps> = ({
  requirement,
  busy,
  error,
  onOpenEdital,
  onUpload,
  onUploadDeclarationFile,
  onRemoveFile,
  onConfirm,
  onIgnore,
  onReopen,
  onSaveDeclaration,
  biddingId,
  editalNumber,
  onUploadGuarantee,
  canQuoteGuarantee = false,
  onOpenQuote,
  sendGuaranteeDocumentRequest,
}) => {
  const fileInput = useRef<HTMLInputElement>(null);
  // Recolhida por padrão (como no protótipo); abre sozinha quando o "Corrigir" da checagem
  // aponta para ela.
  const { focusTarget } = useProposalShell();
  const [aberta, setAberta] = useState(false);
  useEffect(() => {
    if (focusTarget === proposalRequirementTarget(requirement.id)) setAberta(true);
  }, [focusTarget, requirement.id]);
  const statusInfo = REQUIREMENT_STATUS[requirement.status];
  const ignored = requirement.status === 'ignorada';
  const confirmed = requirement.status === 'confirmado';
  const locked = ignored || confirmed;
  const working = Boolean(busy);
  const subtitle = subtitleOf(requirement);

  const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Limpa o input para o mesmo arquivo poder ser escolhido de novo depois de um erro.
    event.target.value = '';
    if (file) onUpload(file);
  };

  const renderFiles = () =>
    requirement.files.length > 0 && (
      <ul className="requirement-files list-unstyled mb-3">
        {requirement.files.map((file) => (
          <li key={file.key} className="d-flex align-items-center py-2">
            <i className="material-icons-outlined mr-2 text-muted" aria-hidden="true">
              description
            </i>
            <div className="flex-grow-1 min-w-0">
              {file.url ? (
                <a href={file.url} target="_blank" rel="noopener noreferrer" className="file-name">
                  {file.name}
                </a>
              ) : (
                <span className="file-name">{file.name}</span>
              )}
              {(file.sentBy || file.sentAt) && (
                <small className="d-block text-muted">
                  Enviado
                  {file.sentBy ? ` por ${file.sentBy}` : ''}
                  {file.sentAt ? ` em ${formatDate(file.sentAt)}` : ''}
                </small>
              )}
            </div>
            {!locked && (
              <button
                type="button"
                className="btn btn-sm btn-link text-danger flex-shrink-0"
                disabled={working}
                onClick={() => onRemoveFile(file.key)}
              >
                {busy === `remove-${file.key}` ? 'Removendo…' : 'Remover'}
              </button>
            )}
          </li>
        ))}
      </ul>
    );

  const renderDocumentActions = () => {
    if (requirement.tipo === 'arquivo') {
      const hasFile = requirement.files.length > 0;
      return (
        <>
          <input
            ref={fileInput}
            type="file"
            accept={PROPOSAL_FILE_ACCEPT}
            className="d-none"
            onChange={handleFile}
          />
          <button
            type="button"
            className={`btn ${hasFile ? 'btn-outline-primary' : 'btn-primary'} mr-2 mb-2`}
            disabled={working}
            onClick={() => fileInput.current?.click()}
          >
            {busy === 'upload' && 'Enviando…'}
            {busy !== 'upload' && (hasFile ? 'Enviar outro arquivo' : 'Enviar documento')}
          </button>
          {hasFile && (
            <button
              type="button"
              className="btn btn-primary mr-2 mb-2"
              disabled={working}
              onClick={onConfirm}
            >
              {busy === 'confirm' ? 'Confirmando…' : 'Confirmar documento'}
            </button>
          )}
        </>
      );
    }

    if (requirement.tipo === 'amostra') {
      return (
        <button
          type="button"
          className="btn btn-primary mr-2 mb-2"
          disabled={working}
          onClick={onConfirm}
        >
          {busy === 'confirm' ? 'Marcando…' : 'Marcar como preparada'}
        </button>
      );
    }

    return null;
  };

  const kindIcon = {
    declaracao: 'history_edu',
    arquivo: 'description',
    amostra: 'inventory_2',
    garantia: 'verified_user',
  }[requirement.tipo];
  const tipoLabel = {
    declaracao: 'Declaração exigida',
    arquivo: 'Documento exigido',
    amostra: 'Amostra exigida',
    garantia: 'Garantia exigida',
  }[requirement.tipo];

  const resumo = (() => {
    if (subtitle) return subtitle;
    if (requirement.guarantee) return requirement.guarantee.valor_exigido || 'Exigida pelo edital';
    if (requirement.declaration) {
      if (requirement.declaration.portal) return 'Assinalar no portal';
      if (!requirement.declaration.html.trim()) {
        return requirement.files.length > 0
          ? 'Declaração enviada em arquivo'
          : 'Declaração não encontrada no edital';
      }
      if (requirement.declaration.origem === 'modelo')
        return 'Declaração feita a partir de um modelo';
      if (requirement.declaration.origem === 'livre') return 'Declaração escrita por você';
      return 'Confira os dados na declaração extraída do edital';
    }
    if (requirement.tipo === 'amostra') return 'Entrega física: o órgão avisa quando precisar';
    const n = requirement.files.length;
    if (n === 0) return 'Adicione o arquivo do documento';
    return n === 1 ? '1 arquivo enviado' : `${n} arquivos enviados`;
  })();

  return (
    <Container
      className={`requirement-card status-${requirement.status}${ignored ? ' is-ignored' : ''}`}
    >
      <div className="requirement-head">
        <button
          type="button"
          className="requirement-title"
          aria-expanded={aberta}
          onClick={() => setAberta((v) => !v)}
        >
          <i className="material-icons-outlined requirement-kind" aria-hidden="true">
            {kindIcon}
          </i>
          <span className="min-w-0">
            <strong title={requirement.name}>{requirement.name}</strong>
            <small>{resumo}</small>
          </span>
        </button>
        {requirement.manual && <span className="requirement-manual">Adicionada por você</span>}
        <span className={`requirement-badge badge-status-${requirement.status}`}>
          {statusInfo.label}
        </span>
        <button
          type="button"
          className="requirement-toggle"
          aria-expanded={aberta}
          aria-label={aberta ? 'Recolher exigência' : 'Abrir exigência'}
          onClick={() => setAberta((v) => !v)}
        >
          <i className={`material-icons-outlined${aberta ? ' rotated' : ''}`} aria-hidden="true">
            expand_more
          </i>
        </button>
      </div>

      {aberta && (
        <div className="requirement-body">
          {(requirement.excerpt || (onOpenEdital && !requirement.manual)) && (
            <section>
              <h3>{tipoLabel}</h3>
              <blockquote>
                {requirement.excerpt && <>&ldquo;{requirement.excerpt}&rdquo;</>}
                {onOpenEdital && !requirement.manual && (
                  <small>
                    <button type="button" onClick={onOpenEdital}>
                      Abrir edital
                      <i className="material-icons-outlined" aria-hidden="true">
                        open_in_new
                      </i>
                    </button>
                  </small>
                )}
              </blockquote>
            </section>
          )}

          {requirement.declaration?.portal && (
            <section>
              <div className="alert alert-warning portal-alert mb-2">
                <i className="material-icons-outlined" aria-hidden="true">
                  how_to_reg
                </i>
                <div>
                  <strong>Assinalar no portal</strong>
                  <span>
                    O edital pede que esta declaração seja assinalada no sistema da licitação, sem
                    enviar arquivo.
                  </span>
                </div>
              </div>
              {!locked && (
                <button
                  type="button"
                  className="btn btn-primary mb-2"
                  disabled={working}
                  onClick={onConfirm}
                >
                  {busy === 'confirm' ? 'Confirmando…' : 'Ciente'}
                </button>
              )}
            </section>
          )}

          {requirement.declaration && !requirement.declaration.portal && (
            <section>
              <input
                ref={fileInput}
                type="file"
                accept={PROPOSAL_FILE_ACCEPT}
                className="d-none"
                onChange={handleFile}
              />
              <AiManagementProposalDeclarationBody
                requirement={requirement}
                locked={locked}
                busy={busy}
                onSave={onSaveDeclaration ?? (() => Promise.resolve())}
                onConfirm={onConfirm}
                onPickFile={() => fileInput.current?.click()}
                onUploadFile={onUploadDeclarationFile}
                biddingId={biddingId}
                editalNumber={editalNumber}
              />
            </section>
          )}

          {requirement.guarantee && (
            <section>
              <AiManagementProposalGuaranteeBody
                guarantee={requirement.guarantee}
                locked={locked}
                busy={busy}
                hasFiles={requirement.files.length > 0}
                canQuote={canQuoteGuarantee}
                onUpload={(file, modalidade) => onUploadGuarantee?.(file, modalidade)}
                onConfirm={onConfirm}
                onOpenQuote={() => onOpenQuote?.()}
                sendDocumentRequest={sendGuaranteeDocumentRequest}
              />
            </section>
          )}

          {renderFiles()}

          {error && <div className="alert alert-danger py-2 mb-0">{error}</div>}

          <div className="d-flex flex-wrap align-items-center requirement-actions">
            {!locked && renderDocumentActions()}
            {locked ? (
              <button
                type="button"
                className="btn btn-outline-secondary mb-2"
                disabled={working}
                onClick={onReopen}
              >
                {busy === 'reopen' ? 'Reabrindo…' : 'Reabrir para editar'}
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-outline-secondary ml-auto mb-2"
                disabled={working}
                onClick={onIgnore}
              >
                {busy === 'ignore' ? 'Ignorando…' : 'Ignorar exigência'}
              </button>
            )}
          </div>
        </div>
      )}
    </Container>
  );
};

export default AiManagementProposalRequirement;
